import {
  ActionConfirmationModal,
  AppModal,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
  Label,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { cn } from "@/lib/utils/cn";
import { type ReportPresetPreviewFilter } from "@/lib/utils/report-filter-presets";
import { Check, ChevronDown, Save, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

type ReportPresetControlsProps = {
  presetDescription: string;
  presetName: string;
  presetOptions: Array<{ description?: string; label: string; value: string }>;
  presetValue?: string;
  saveButtonLabel?: string;
  saveDescription?: string;
  saveDescriptionPlaceholder?: string;
  savePreviewColumns?: string[];
  savePreviewFilters?: ReportPresetPreviewFilter[];
  savePlaceholder?: string;
  saveTitle?: string;
  selectId: string;
  selectLabel?: string;
  selectPlaceholder?: string;
  isPresetListLoading?: boolean;
  isSaveOpen: boolean;
  isSaving?: boolean;
  isDeleting?: boolean;
  onPresetDescriptionChange: (value: string) => void;
  onPresetNameChange: (value: string) => void;
  onDeletePresetValue: (value: string) => void | Promise<void>;
  onPresetValueChange: (value: string) => void | Promise<void>;
  onSave: () => void | Promise<void>;
  onSaveOpenChange: (open: boolean) => void;
};

export function ReportPresetControls({
  presetDescription,
  presetName,
  presetOptions,
  presetValue,
  saveButtonLabel = "Save preset",
  saveDescription = "Save the current filters and visible columns and order for this report.",
  saveDescriptionPlaceholder = "Add a description",
  savePreviewColumns = [],
  savePreviewFilters = [],
  savePlaceholder = "Preset name",
  saveTitle = "Save report preset",
  selectId,
  selectLabel = "Presets",
  selectPlaceholder = "Select a preset",
  isPresetListLoading,
  isSaveOpen,
  isSaving,
  isDeleting,
  onPresetDescriptionChange,
  onPresetNameChange,
  onDeletePresetValue,
  onPresetValueChange,
  onSave,
  onSaveOpenChange,
}: ReportPresetControlsProps) {
  const [isPresetMenuOpen, setIsPresetMenuOpen] = useState(false);
  const [pendingDeletePresetValue, setPendingDeletePresetValue] = useState<
    string | undefined
  >();
  const selectedPresetLabel = useMemo(
    () => presetOptions.find((preset) => preset.value === presetValue)?.label,
    [presetOptions, presetValue],
  );
  const pendingDeletePreset = useMemo(
    () =>
      presetOptions.find((preset) => preset.value === pendingDeletePresetValue),
    [pendingDeletePresetValue, presetOptions],
  );
  const isSaveDirty =
    presetName.trim().length > 0 || presetDescription.trim().length > 0;

  return (
    <>
      <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
        <div className="w-full sm:w-[260px]">
          <Label htmlFor={selectId} className="sr-only">
            {selectLabel}
          </Label>
          <DropdownMenu
            open={isPresetMenuOpen}
            onOpenChange={setIsPresetMenuOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                id={selectId}
                variant="outline"
                className="h-11 w-full justify-between px-3 font-normal"
              >
                <span
                  className={cn(
                    "truncate text-left",
                    selectedPresetLabel
                      ? "text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  {isPresetListLoading
                    ? "Loading presets..."
                    : (selectedPresetLabel ?? selectPlaceholder)}
                </span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[320px]">
              {isPresetListLoading ? (
                <div className="text-muted-foreground px-2 py-1.5 text-sm">
                  Loading presets...
                </div>
              ) : presetOptions.length > 0 ? (
                presetOptions.map((preset) => (
                  <div
                    key={preset.value}
                    className="hover:bg-light-primary focus-within:bg-light-primary flex items-center gap-2 rounded-sm px-2 py-1"
                  >
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-start gap-2 rounded-sm py-1 text-left text-sm"
                      onClick={() => {
                        setIsPresetMenuOpen(false);
                        void onPresetValueChange(preset.value);
                      }}
                    >
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          preset.value === presetValue
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <span className="block truncate">{preset.label}</span>
                        {preset.description ? (
                          <TooltipWrapper message={preset.description}>
                            <div className="max-w-full">
                              <p className="text-muted-foreground line-clamp-3 text-xs whitespace-normal">
                                {preset.description}
                              </p>
                            </div>
                          </TooltipWrapper>
                        ) : null}
                      </div>
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive h-8 w-8 shrink-0"
                      disabled={Boolean(isDeleting)}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsPresetMenuOpen(false);
                        setPendingDeletePresetValue(preset.value);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground px-2 py-1.5 text-sm">
                  No presets saved yet
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Button
          className="h-11 shrink-0"
          variant="outline"
          onClick={() => {
            onSaveOpenChange(true);
          }}
        >
          <Save className="h-4 w-4" />
          {saveButtonLabel}
        </Button>
      </div>
      <ActionConfirmationModal
        open={Boolean(pendingDeletePreset)}
        setOpen={(open) => {
          if (!open) {
            setPendingDeletePresetValue(undefined);
          }
        }}
        title={`Are you sure you want to delete "${pendingDeletePreset?.label ?? "this preset"}"?`}
        description="This report preset will be removed and cannot be recovered."
        buttonText="Delete preset"
        afterActionFn={async () => {
          if (!pendingDeletePreset) return;
          await onDeletePresetValue(pendingDeletePreset.value);
          setPendingDeletePresetValue(undefined);
        }}
        onCancel={() => {
          setPendingDeletePresetValue(undefined);
        }}
      />
      <AppModal
        open={isSaveOpen}
        onOpenChange={onSaveOpenChange}
        title={saveTitle}
        description={saveDescription}
        isDirty={isSaveDirty}
        loading={Boolean(isSaving)}
        size="2xl"
        className="w-[calc(100vw-2rem)] max-w-3xl"
        footer={
          <div className="flex w-full flex-wrap justify-end gap-2">
            <FormCancelButton
              setOpen={onSaveOpenChange}
              isDirty={isSaveDirty}
            />
            <Button
              onClick={() => {
                void onSave();
              }}
              disabled={Boolean(isSaving)}
            >
              {saveButtonLabel}
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          <p className="text-muted-foreground text-sm" aria-hidden>
            {saveDescription}
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`${selectId}-name`}>Preset name</Label>
              <Input
                id={`${selectId}-name`}
                value={presetName}
                onChange={(event) => {
                  onPresetNameChange(event.target.value);
                }}
                placeholder={savePlaceholder}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void onSave();
                  }
                }}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor={`${selectId}-description`}>Description</Label>
              <Textarea
                id={`${selectId}-description`}
                value={presetDescription}
                onChange={(event) => {
                  onPresetDescriptionChange(event.target.value);
                }}
                placeholder={saveDescriptionPlaceholder}
                rows={3}
              />
            </div>
          </div>
          <Separator />
          <Accordion
            type="single"
            collapsible
            className="border-border/60 rounded-xl border"
          >
            <AccordionItem value="filters" className="border-b-0">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-start gap-3 pr-4">
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">Filters</p>
                      <Badge variant="secondary">
                        {savePreviewFilters.length}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-sm font-normal">
                      Active filters will be saved with this preset.
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                {savePreviewFilters.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {savePreviewFilters.map((filter, index) => (
                      <Badge
                        key={`${filter.label}-${filter.description ?? index}`}
                        variant="outline"
                        className="py-1"
                      >
                        {filter.label}
                        {filter.description ? `: ${filter.description}` : ""}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No active filters. The preset will save the unfiltered view.
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <Accordion
            type="single"
            collapsible
            className="border-border/60 rounded-xl border"
          >
            <AccordionItem value="columns" className="border-b-0">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-start gap-3 pr-4">
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">Columns</p>
                      <Badge variant="secondary">
                        {savePreviewColumns.length}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-sm font-normal">
                      Visible columns and their order will be saved with this
                      preset.
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                {savePreviewColumns.length > 0 ? (
                  <div className="border-border/60 overflow-hidden rounded-lg border">
                    <Table className="[&_tr:hover]:bg-transparent">
                      <TableHeader className="bg-muted/30">
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="h-10 w-20">Order</TableHead>
                          <TableHead className="h-10">Column</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {savePreviewColumns.map((column, index) => (
                          <TableRow key={`${column}-${index}`}>
                            <TableCell className="text-muted-foreground w-20 font-mono text-xs">
                              {index + 1}
                            </TableCell>
                            <TableCell className="font-medium">
                              {column}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No visible columns are selected.
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </AppModal>
    </>
  );
}
